import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ChevronRight, Zap, RefreshCw } from 'lucide-react';
import { T, springs } from '../lib/tokens';
import { fmtRelative } from '../lib/helpers';
import { Glass } from '../components/glass/Glass';
import { Btn } from '../components/primitives/Btn';
import { Pill } from '../components/primitives/Badge';
import { Md } from '../components/primitives/Md';
import { isLocalAvailable, apiFetch } from '../lib/api';

const RESEARCH_TYPES = [
  { id: 'investor',    label: 'Investor',    color: '#D4921E', bg: 'rgba(212,146,30,0.10)'  },
  { id: 'creator',     label: 'Creator',     color: '#4A9ECC', bg: 'rgba(74,158,204,0.10)'  },
  { id: 'competitive', label: 'Competitor',  color: '#8B72C8', bg: 'rgba(139,114,200,0.10)' },
  { id: 'general',     label: 'General',     color: '#5A9E78', bg: 'rgba(90,158,120,0.10)'  },
];

const QUICK_SEARCHES = [
  'Rashid Khan UCEA Capital',
  'Araya Ventures',
  'Dyrectli Capital',
  'Stefan Glaenzer',
  'Tim Jackson Walking Ventures',
];

export const ResearchView = ({ inbox }) => {
  const [query, setQuery]       = useState('');
  const [type, setType]         = useState('investor');
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState('');

  // Past research briefs from inbox
  const pastBriefs = inbox
    .filter(i => i.from_name === 'Research Agent' || i.subject?.startsWith('Research:'))
    .slice(0, 10);

  const runResearch = async (subject) => {
    const q = subject || query.trim();
    if (!q) return;
    setLoading(true);
    setResult(null);
    setError('');

    if (!isLocalAvailable()) { setError(q); setLoading(false); return; }
    try {
      const res = await apiFetch(`/research?subject=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error('Research server not available');
      const data = await res.json();
      setResult({ subject: q, body: data.output, timestamp: new Date() });
    } catch {
      setError(q);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: T.text, letterSpacing: '-0.03em' }}>Research</h1>
        <p style={{ fontSize: 14, color: T.textMuted, marginTop: 4 }}>
          Instant briefings on investors, creators, and competitors.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}>
        {/* Main search area */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Search input */}
          <Glass style={{ padding: 24 }} hover={false}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, marginBottom: 12, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Research subject
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <div style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                background: 'rgba(0,0,0,0.03)', borderRadius: 10,
                border: '1px solid rgba(0,0,0,0.08)',
                padding: '10px 14px',
              }}>
                <Search size={15} color={T.textMuted} />
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && runResearch()}
                  placeholder="e.g. Rashid Khan UCEA Capital, Araya Ventures..."
                  style={{
                    flex: 1, border: 'none', background: 'transparent',
                    fontSize: 14, color: T.text, outline: 'none',
                    fontFamily: 'inherit',
                  }}
                />
                {query && (
                  <button onClick={() => setQuery('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.textLight, fontSize: 16, lineHeight: 1 }}>x</button>
                )}
              </div>
              <Btn variant="primary" onClick={() => runResearch()} style={{ flexShrink: 0, padding: '10px 20px' }}>
                {loading ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={14} />}
                {loading ? 'Researching...' : 'Research'}
              </Btn>
            </div>

            {/* Type selector */}
            <div style={{ display: 'flex', gap: 8 }}>
              {RESEARCH_TYPES.map(t => (
                <Pill key={t.id} active={type === t.id} color={t.color} bg={t.bg} border={t.color + '40'} onClick={() => setType(t.id)}>
                  {t.label}
                </Pill>
              ))}
            </div>
          </Glass>

          {/* Loading state */}
          <AnimatePresence>
            {loading && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <Glass style={{ padding: 48, textAlign: 'center' }} hover={false}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: T.amberLight, border: `1px solid ${T.amberBorder}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 20px',
                    animation: 'spin 3s linear infinite',
                  }}>
                    <Search size={22} color={T.amber} />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 6 }}>
                    Researching...
                  </div>
                  <div style={{ fontSize: 13, color: T.textMuted }}>
                    Reading Gmail, checking networks, building brief.
                  </div>
                </Glass>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error / terminal fallback */}
          {error && !loading && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <Glass style={{ padding: 24, background: 'rgba(212,146,30,0.06)', border: '1px solid rgba(212,146,30,0.20)' }} hover={false}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: T.amberLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Zap size={15} color={T.amber} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>
                      Run this in Terminal
                    </div>
                    <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.5 }}>
                      The local research server isn't running. Paste this command into Terminal and the brief will appear in your Inbox.
                    </div>
                  </div>
                </div>
                <div style={{
                  background: '#1a1a2e', borderRadius: 10, padding: '14px 18px',
                  fontFamily: 'monospace', fontSize: 14, color: '#e8e8f0',
                  letterSpacing: '0.01em', userSelect: 'all',
                }}>
                  research "{error}"
                </div>
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 10 }}>
                  Brief will appear in Inbox once complete. Refresh to see it.
                </div>
              </Glass>
            </motion.div>
          )}

          {/* Result */}
          <AnimatePresence>
            {result && !loading && (
              <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} transition={springs.gentle}>
                <Glass style={{ padding: 28 }} hover={false}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: T.amberLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Search size={15} color={T.amber} />
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{result.subject}</div>
                      <div style={{ fontSize: 12, color: T.textMuted }}>{fmtRelative(result.timestamp)}</div>
                    </div>
                  </div>
                  <Md>{result.body}</Md>
                </Glass>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Past briefs from inbox */}
          {!result && !loading && !error && pastBriefs.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 14 }}>
                Recent Research
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pastBriefs.map((brief, i) => (
                  <motion.div key={brief.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03, ...springs.gentle }}>
                    <Glass style={{ padding: '16px 20px', cursor: 'pointer' }}
                      onClick={() => setResult({ subject: brief.subject.replace('Research: ', ''), body: brief.body, timestamp: new Date(brief.created_at) })}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: T.text, marginBottom: 3 }}>
                            {brief.subject.replace('Research: ', '')}
                          </div>
                          <div style={{ fontSize: 12, color: T.textMuted }}>
                            {brief.preview || brief.body?.slice(0, 100)}
                          </div>
                        </div>
                        <span style={{ fontSize: 11, color: T.textLight, marginLeft: 16, flexShrink: 0 }}>
                          {fmtRelative(brief.created_at)}
                        </span>
                      </div>
                    </Glass>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!result && !loading && !error && pastBriefs.length === 0 && (
            <Glass style={{ padding: 56, textAlign: 'center' }} hover={false}>
              <Search size={36} color={T.textLight} style={{ marginBottom: 14 }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: T.textMid, marginBottom: 6 }}>
                Research anything, instantly.
              </div>
              <div style={{ fontSize: 14, color: T.textMuted, maxWidth: 380, margin: '0 auto' }}>
                Type an investor name, creator handle, or competitor and get a structured brief in 60 seconds.
              </div>
            </Glass>
          )}
        </div>

        {/* Right panel — quick searches + tips */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Glass style={{ padding: 20 }} hover={false}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 14 }}>
              Quick Search
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {QUICK_SEARCHES.map(s => (
                <button key={s} onClick={() => { setQuery(s); runResearch(s); }} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
                  background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.06)',
                  fontSize: 13, color: T.textMid, fontFamily: 'inherit',
                  transition: 'all 0.15s', textAlign: 'left',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = T.amberLight; e.currentTarget.style.color = T.amber; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.02)'; e.currentTarget.style.color = T.textMid; }}>
                  {s}
                  <ChevronRight size={13} />
                </button>
              ))}
            </div>
          </Glass>

          <Glass style={{ padding: 20 }} hover={false}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 14 }}>
              What you can research
            </div>
            {[
              ['Investors', 'Thesis, portfolio, Liken fit, suggested opener'],
              ['Creators', 'Profile, outreach angle, draft message'],
              ['Competitors', 'Product, differentiation, threat level'],
              ['General', 'Background, facts, relevance to Liken'],
            ].map(([label, desc]) => (
              <div key={label} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{label}</div>
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{desc}</div>
              </div>
            ))}
          </Glass>

          <Glass style={{ padding: 20, background: 'rgba(212,146,30,0.05)', border: '1px solid rgba(212,146,30,0.15)' }} hover={false}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.amber, marginBottom: 8 }}>
              Terminal shortcut
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 12, color: T.textMid, background: 'rgba(0,0,0,0.04)', borderRadius: 6, padding: '8px 10px' }}>
              research "name or company"
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 8, lineHeight: 1.5 }}>
              Results appear here automatically via Supabase realtime.
            </div>
          </Glass>
        </div>
      </div>
    </motion.div>
  );
};
